/**
 * HTML builders for Sponsor Run participant sheets and staff answer keys.
 */

import { getLogoUrl } from './printUtils';

export function escapeHtml(s) {
  return String(s ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

const INTRO_PARAS = [
  'Welcome to the Sponsor Run!',
  'Take this sheet to the vendors listed below and answer the questions AS A COMPLETE TEAM.',
  'You can use the Sponsor Alley map included in your package to locate them.',
  'If you answer correctly, you will receive a vendor chip. Bring the chips you received to FreshMeet HQ to receive your next dive.',
];

export function getSponsorRunPrintCss() {
  return `
    @page { size: letter; margin: 0.5in; }
    body {
      font-family: system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif;
      font-size: 11pt;
      color: #1c1917;
      line-height: 1.4;
      background: #fafaf9;
    }
    .sr-page {
      page-break-after: always;
      min-height: 9in;
      border-top: 5pt solid #f47b20;
      padding-top: 0.5rem;
      margin-bottom: 0;
      background: #fff;
      border-radius: 2px;
      box-shadow: 0 1px 3px rgba(0,0,0,0.06);
    }
    .sr-page:last-child { page-break-after: auto; }
    .sr-page-banner {
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: 0.75rem;
      flex-wrap: wrap;
      margin-bottom: 0.65rem;
      padding-bottom: 0.45rem;
      border-bottom: 2px solid #ffedd5;
    }
    .sr-page-banner-event {
      font-size: 10.5pt;
      font-weight: 700;
      color: #c2410c;
      letter-spacing: 0.03em;
      text-transform: uppercase;
    }
    .sr-fm-logo-print {
      height: 26pt;
      width: auto;
      max-width: 1.6in;
      object-fit: contain;
    }
    .sr-intro {
      margin-bottom: 0.85rem;
      padding: 0.5rem 0.55rem;
      background: linear-gradient(135deg, #fff7ed 0%, #fffbeb 100%);
      border: 1px solid #fed7aa;
      border-radius: 6px;
    }
    .sr-intro p { margin: 0 0 0.4rem 0; }
    .sr-intro p:last-child { margin-bottom: 0; }
    .sr-set-head {
      font-size: 12pt;
      font-weight: 800;
      color: #431407;
      margin: 0 0 0.65rem 0;
      padding-bottom: 0.3rem;
      border-bottom: 3pt solid #f47b20;
      letter-spacing: 0.02em;
    }
    .sr-columns { display: grid; grid-template-columns: 1fr 1fr; gap: 1rem 1.1rem; align-items: start; }
    .sr-columns--single { grid-template-columns: 1fr; }
    .sr-sponsor-card {
      border: 1px solid #e7e5e4;
      border-radius: 8px;
      padding: 0.55rem 0.55rem 0.6rem;
      background: linear-gradient(180deg, #fffefb 0%, #ffffff 45%);
      break-inside: avoid;
      page-break-inside: avoid;
    }
    .sr-sponsor-body {
      display: grid;
      grid-template-columns: minmax(72pt, 0.95in) 1fr;
      gap: 0.5rem 0.65rem;
      align-items: start;
    }
    .sr-sponsor-meta {
      display: flex;
      flex-direction: column;
      align-items: flex-start;
      gap: 0.28rem;
      min-width: 0;
    }
    .sr-sponsor-logo-wrap {
      width: 100%;
      max-width: 0.9in;
      aspect-ratio: 1;
      display: flex;
      align-items: center;
      justify-content: center;
      border: 1px solid #e7e5e4;
      border-radius: 8px;
      background: #fff;
      padding: 3px;
      box-sizing: border-box;
    }
    .sr-sponsor-logo {
      max-width: 100%;
      max-height: 100%;
      width: auto;
      height: auto;
      object-fit: contain;
    }
    .sr-sponsor-name {
      font-size: 10.5pt;
      font-weight: 800;
      margin: 0;
      color: #1c1917;
      line-height: 1.2;
      word-break: break-word;
    }
    .sr-sponsor-url {
      font-size: 8.5pt;
      color: #1d4ed8;
      word-break: break-all;
      line-height: 1.25;
      margin: 0;
    }
    .sr-sponsor-url a { color: inherit; text-decoration: none; }
    .sr-sponsor-q { min-width: 0; }
    .sr-q-list {
      margin: 0;
      padding-left: 1.35rem;
      font-size: 13pt;
      line-height: 1.4;
      font-weight: 600;
      color: #1c1917;
    }
    .sr-q-list li { margin-bottom: 0.55rem; }
    .sr-q-list li:last-child { margin-bottom: 0; }
    .sr-answer {
      font-size: 10.5pt;
      color: #047857;
      margin-top: 0.3rem;
      margin-left: 0;
      padding-left: 0;
      list-style: none;
      font-weight: 600;
      line-height: 1.35;
    }
    .sr-answer-key-meta { font-size: 9pt; color: #6b7280; margin-bottom: 0.15rem; }
    .sr-empty { color: #9ca3af; font-style: italic; font-size: 10pt; }
    .sr-sponsor-stack { display: flex; flex-direction: column; gap: 0.65rem; }
    @media print {
      body { background: #fff; }
      .sr-page { box-shadow: none; border-radius: 0; }
      .sr-intro,
      .sr-sponsor-card {
        -webkit-print-color-adjust: exact;
        print-color-adjust: exact;
      }
    }
  `;
}

function normalizeWebsite(s) {
  const w = (s || '').toString().trim();
  if (!w) return '';
  if (/^https?:\/\//i.test(w)) return w;
  return `https://${w}`;
}

function sortSponsorsInSet(sponsors, setAssignments, setId) {
  return (sponsors || [])
    .filter((s) => setAssignments[s.id] === setId)
    .sort((a, b) => (a.displayOrder ?? 0) - (b.displayOrder ?? 0));
}

function chunkPairs(list) {
  const chunks = [];
  for (let i = 0; i < list.length; i += 2) {
    chunks.push(list.slice(i, i + 2));
  }
  return chunks;
}

function buildIntroHtml() {
  const paras = INTRO_PARAS.map((t) => `<p>${escapeHtml(t)}</p>`).join('');
  return `<div class="sr-intro">${paras}</div>`;
}

function buildSponsorBlock(sponsor, questionIds, questionsById, mode) {
  const nameRaw = sponsor.name || sponsor.companyName || 'Sponsor';
  const name = escapeHtml(nameRaw);
  const rawSite = (sponsor.website || '').toString().trim();
  const url = rawSite ? normalizeWebsite(rawSite) : '';
  const urlDisplay = escapeHtml(rawSite.replace(/^https?:\/\//i, ''));
  const logoRaw = (sponsor.logo || '').toString().trim();
  const logoSrc = logoRaw ? escapeHtml(logoRaw) : '';
  const logoAlt = escapeHtml(nameRaw);
  const logoBlock = logoSrc
    ? `<div class="sr-sponsor-logo-wrap"><img class="sr-sponsor-logo" src="${logoSrc}" alt="${logoAlt}" /></div>`
    : '';
  const qids = Array.isArray(questionIds) ? questionIds : [];
  let qHtml = '';
  if (qids.length === 0) {
    qHtml = '<p class="sr-empty">No questions assigned yet.</p>';
  } else {
    const items = qids.map((qid, idx) => {
      const q = questionsById[qid];
      const prompt = q ? escapeHtml(q.prompt) : `(${escapeHtml(qid)} — missing from bank)`;
      if (mode === 'answerKey') {
        const ans = q && q.answer != null && String(q.answer).trim() ? escapeHtml(String(q.answer).trim()) : '—';
        return `<li><div>${prompt}</div><div class="sr-answer">Answer: ${ans}</div></li>`;
      }
      return `<li>${prompt}</li>`;
    });
    qHtml = `<ol class="sr-q-list">${items.join('')}</ol>`;
  }
  const urlBlock = url
    ? `<div class="sr-sponsor-url"><a href="${escapeHtml(url)}">${urlDisplay}</a></div>`
    : '';
  return `
    <div class="sr-sponsor-card">
      <div class="sr-sponsor-body">
        <div class="sr-sponsor-meta">
          ${logoBlock}
          <div class="sr-sponsor-name">${name}</div>
          ${urlBlock}
        </div>
        <div class="sr-sponsor-q">${qHtml}</div>
      </div>
    </div>
  `;
}

/**
 * @param {Object} opts
 * @param {string} opts.eventTitle
 * @param {Array<{id: string, name?: string, companyName?: string, website?: string, logo?: string, displayOrder?: number}>} opts.sponsors
 * @param {Record<string, 'A'|'B'|'C'>} opts.setAssignments
 * @param {Record<string, string[]>} opts.sponsorQuestionIds
 * @param {Record<string, { id: string, prompt?: string, answer?: string }>} opts.questionsById
 * @param {'participant'|'answerKey'} opts.mode
 * @param {('A'|'B'|'C')[]|null} opts.setsOnly - if set, only these sets (order preserved)
 */
export function buildSponsorRunSheetsHtml(opts) {
  const {
    eventTitle = '',
    sponsors = [],
    setAssignments = {},
    sponsorQuestionIds = {},
    questionsById = {},
    mode = 'participant',
    setsOnly = null,
  } = opts;

  const setOrder = setsOnly && setsOnly.length ? setsOnly : ['A', 'B', 'C'];
  const setLabels = { A: 'Set A', B: 'Set B', C: 'Set C' };

  const brandLogo =
    typeof window !== 'undefined' && window.location?.origin ? getLogoUrl() : '';
  const bannerLogo = brandLogo
    ? `<img class="sr-fm-logo-print" src="${escapeHtml(brandLogo)}" alt="" />`
    : '';

  let pages = '';

  for (const setId of setOrder) {
    const inSet = sortSponsorsInSet(sponsors, setAssignments, setId);
    if (inSet.length === 0) continue;

    const pairs = chunkPairs(inSet);
    for (const pair of pairs) {
      const colClass = pair.length >= 2 ? 'sr-columns' : 'sr-columns sr-columns--single';
      const sponsorCols = pair
        .map((sp) => buildSponsorBlock(sp, sponsorQuestionIds[sp.id] || [], questionsById, mode))
        .join('');
      const setHead =
        mode === 'answerKey'
          ? `<h2 class="sr-set-head">${escapeHtml(setLabels[setId] || setId)} — ${escapeHtml(eventTitle || 'Sponsor Run')} — Answer key</h2>`
          : `<h2 class="sr-set-head">${escapeHtml(setLabels[setId] || setId)}</h2>`;
      const bannerEvent = escapeHtml((eventTitle || 'Sponsor Run').trim() || 'Sponsor Run');

      pages += `
        <div class="sr-page">
          <div class="sr-page-banner">
            <span class="sr-page-banner-event">${bannerEvent}</span>
            ${bannerLogo}
          </div>
          ${buildIntroHtml()}
          ${setHead}
          <div class="${colClass}">
            ${sponsorCols}
          </div>
        </div>
      `;
    }
  }

  if (!pages.trim()) {
    return `<div class="sr-page"><p class="sr-empty">No interactive sponsors assigned to sets A/B/C for this event.</p></div>`;
  }

  return pages;
}
